import cn from 'classnames';
import { FC, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildTemplatesUrl } from '@/shared/lib';
import { Button, NavCount, NavItem, NavList } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import {
  createTemplatesNavigationState,
  getPathWithSearch,
  parseTemplatesPath,
  resolveTemplatesBackPath,
  templatesModel,
} from '@/entities/templates';

import { TEMPLATE_LIST_PARAM, templateListMessages } from '../../config';
import { useTemplateLanguageFilter } from '../../hooks';
import {
  filterTemplateDataByLanguage,
  getTemplateParentSchema,
  getTemplateSpecialtyGroups,
} from '../../lib';

import styles from './TemplatesSidebar.module.scss';

type TemplatesSidebarProps = {
  className?: string;
  pathname?: string;
};

export const TemplatesSidebar: FC<TemplatesSidebarProps> = (props) => {
  const { className, pathname = '/templates' } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const editorParam = searchParams.get('editor');
  const editorIdParam = searchParams.get('id');
  const isSchemaEditor = editorParam === 'schema' && Boolean(editorIdParam);
  const isDocumentEditor = editorParam === 'document' && Boolean(editorIdParam);

  const allSchemas = useAppSelector(
    templatesModel.selectors.selectEncounterSchemas,
  );
  const encounterSchemasListError = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasListError,
  );
  const allTemplates = useAppSelector(
    templatesModel.selectors.selectAllDocumentTemplates,
  );

  const { language } = useTemplateLanguageFilter();
  const { schemas, templates } = useMemo(
    () =>
      filterTemplateDataByLanguage({
        language,
        schemas: allSchemas,
        templates: allTemplates,
      }),
    [allSchemas, allTemplates, language],
  );

  const { groups } = useMemo(
    () => getTemplateSpecialtyGroups(schemas, templates),
    [schemas, templates],
  );

  const selectedFromQuery = searchParams.get(TEMPLATE_LIST_PARAM.specialty);
  const currentTemplatesPath = resolveTemplatesBackPath(
    location.state,
    getPathWithSearch(pathname, location.search),
  );
  const activeTemplate = isDocumentEditor
    ? allTemplates.find((template) => template.id === editorIdParam)
    : undefined;
  const isDetailMode = isSchemaEditor || isDocumentEditor;
  const activeTemplateSchemaId = useMemo(() => {
    if (!activeTemplate) {
      return null;
    }

    const { groups: activeTemplateGroups } = getTemplateSpecialtyGroups(
      allSchemas,
      [activeTemplate],
    );

    return (
      getTemplateParentSchema(activeTemplate, activeTemplateGroups)?.id ?? null
    );
  }, [activeTemplate, allSchemas]);

  const selectedSchemaId =
    (isSchemaEditor ? editorIdParam : null) ??
    activeTemplateSchemaId ??
    selectedFromQuery ??
    null;
  const selectedSchemaLoaded = useMemo(
    () =>
      selectedFromQuery
        ? allSchemas.some((schema) => schema.id === selectedFromQuery)
        : false,
    [allSchemas, selectedFromQuery],
  );

  useEffect(() => {
    if (!selectedFromQuery || selectedSchemaLoaded) {
      return;
    }

    void dispatch(
      templatesModel.fetchEncounterSchemaById({
        id: selectedFromQuery,
        organizationId,
      }),
    );
  }, [selectedFromQuery, selectedSchemaLoaded, organizationId]);

  const getTargetUrl = (schemaId: string | null) => {
    const baseContextPath = isDetailMode
      ? currentTemplatesPath
      : getPathWithSearch(pathname, location.search);
    const { pathname: basePath, searchParams: baseSearchParams } =
      parseTemplatesPath(baseContextPath);
    const nextSearchParams = new URLSearchParams(baseSearchParams);

    if (schemaId) {
      nextSearchParams.set('specialty', schemaId);
    } else {
      nextSearchParams.delete('specialty');
    }

    const search = nextSearchParams.toString();

    return `${basePath}${search ? `?${search}` : ''}`;
  };

  return (
    <aside
      className={cn(styles.container, className)}
      data-component="templates-sidebar"
    >
      <div className={styles.header}>
        <div className={cn('h6', styles.headerTitle)}>
          {formatMessage(templateListMessages.specialties)}
        </div>

        <Button
          variant="tertiary"
          size="small"
          icon="plus"
          className={styles.addButton}
          onClick={() =>
            navigate(buildTemplatesUrl({ editor: 'schema', mode: 'new' }), {
              state: createTemplatesNavigationState(currentTemplatesPath),
            })
          }
        >
          {formatMessage(templateListMessages.createSpecialty)}
        </Button>
      </div>

      {encounterSchemasListError ? (
        <div className={styles.notice}>
          <div className={cn('p4b', styles.noticeTitle)}>
            {formatMessage(templateListMessages.encounterSchemasUnavailable)}
          </div>
          <div className={cn('p4', styles.noticeText)}>
            {formatMessage(
              selectedSchemaLoaded
                ? templateListMessages.encounterSchemasSelectedOnlyDescription
                : templateListMessages.encounterSchemasUnavailableDescription,
              {
                error: encounterSchemasListError,
              },
            )}
          </div>
        </div>
      ) : null}

      <NavList className={styles.list}>
        <NavItem
          to={getTargetUrl(null)}
          state={createTemplatesNavigationState(getTargetUrl(null))}
          active={!selectedSchemaId}
          suffix={<NavCount>{templates.length}</NavCount>}
        >
          {formatMessage(templateListMessages.allSpecialties)}
        </NavItem>

        {groups.map(({ schema, templates: schemaTemplates }) => (
          <NavItem
            key={schema.id}
            to={getTargetUrl(schema.id)}
            state={createTemplatesNavigationState(getTargetUrl(schema.id))}
            active={selectedSchemaId === schema.id}
            suffix={<NavCount>{schemaTemplates.length}</NavCount>}
          >
            {schema.name}
          </NavItem>
        ))}
      </NavList>
    </aside>
  );
};
